/**
 * TranslateDialog — Translates a caption + hashtags to target languages (default EN/IT/FR).
 * Accepts source text, shows results, one-click copy.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Languages, Copy, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AVAILABLE = [
  { code: "en", label: "English" },
  { code: "it", label: "Italiano" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
];

export default function TranslateDialog({ text, open, onOpenChange }) {
  const [selected, setSelected] = useState(["en", "it", "fr"]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [copied, setCopied] = useState(null);

  const toggle = (code) => {
    setSelected((p) => p.includes(code) ? p.filter((c) => c !== code) : [...p, code]);
  };

  const run = async () => {
    if (!text?.trim()) {
      toast.info("Kein Text zum Uebersetzen");
      return;
    }
    if (selected.length === 0) {
      toast.info("Waehle mindestens eine Sprache");
      return;
    }
    setLoading(true);
    setResults(null);
    try {
      const res = await axios.post(
        `${API}/ai/translate`,
        { text, targets: selected, source: "de" },
        { withCredentials: true }
      );
      setResults(res.data.translations || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Uebersetzung fehlgeschlagen");
    }
    setLoading(false);
  };

  const copy = async (code, txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      setCopied(code);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error("Clipboard fehlgeschlagen");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="translate-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Languages className="w-5 h-5 text-[#2563EB]" /> Uebersetzen
          </DialogTitle>
        </DialogHeader>

        <div className="bg-[var(--studio-bg)] rounded-lg p-3 text-sm">
          <p className="text-xs text-[var(--studio-text-muted)] mb-1">Original (DE)</p>
          <p className="whitespace-pre-wrap">{text || "—"}</p>
        </div>

        <div>
          <p className="text-xs text-[var(--studio-text-muted)] mb-2">Zielsprachen</p>
          <div className="flex flex-wrap gap-2">
            {AVAILABLE.map((l) => (
              <button
                key={l.code}
                onClick={() => toggle(l.code)}
                data-testid={`lang-${l.code}`}
                className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                  selected.includes(l.code)
                    ? "bg-[#2563EB] text-white border-[#2563EB]"
                    : "bg-white border-[var(--studio-border)] hover:border-[#2563EB]"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        <Button
          onClick={run}
          disabled={loading}
          className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
          data-testid="translate-run-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Languages className="w-4 h-4 mr-2" />}
          Uebersetzen
        </Button>

        {results && (
          <div className="space-y-3" data-testid="translate-results">
            {Object.entries(results).map(([code, txt]) => (
              <div key={code} className="border border-[var(--studio-border)] rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-semibold uppercase">{code}</p>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => copy(code, txt)}
                    data-testid={`copy-${code}`}
                  >
                    {copied === code ? <><Check className="w-3 h-3 mr-1" /> Kopiert</> : <><Copy className="w-3 h-3 mr-1" /> Kopieren</>}
                  </Button>
                </div>
                <p className="text-sm whitespace-pre-wrap">{txt}</p>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
